import apiService, { getToken } from './api';
import type { ApiResponse } from '../types';
import { ApiError } from '../types/api';
import type { NumericValue } from '@/utils/numeric';

// 'personal' is the canonical sick-leave type. The legacy 'sick' enum value
// was folded into 'personal' — see migration 20260521-02-deprecate-sick-leave-type.sql.
export type LeaveType = 'annual' | 'personal' | 'unpaid' | 'other' | 'time_in_lieu' | 'long_service';
export type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

/**
 * Approved leave already sitting on a day, as reported by a refused clock-in
 * (`409 LEAVE_ON_THIS_DAY`) or alongside a successful one (`leave_notice`).
 *
 * Leave is approved ahead of time and writes its timesheet rows straight away.
 * When the plan changes and the employee works the day instead, this is what
 * the app needs in order to offer to release it rather than leave them with a
 * clock-out that can never succeed.
 */
export interface LeaveOnDay {
  leave_request_id: number;
  leave_type: LeaveType;
  date: string;
  /** A partial day carries explicit times; a full day covers the whole roster. */
  is_partial_day: boolean;
  start_time: string | null;
  end_time: string | null;
  hours: number;
  entry_ids: number[];
}

export interface ReleaseLeaveDayResult {
  leave_request: LeaveRequest;
  released_date: string;
  released_hours: number;
  /** False when other days of a multi-day request are still being taken. */
  fully_withdrawn: boolean;
  remaining_dates: string[];
}

export interface LeaveRequest {
  id: number;
  user_id: number;
  organisation_id: number;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  total_days: number;
  hours?: number;
  /** Partial-day leave window (HH:mm:ss from Postgres TIME). Null for full days. */
  start_time?: string | null;
  end_time?: string | null;
  /** Total scheduled work hours covered by this leave period (calculated from work schedule at submission time) */
  scheduled_hours?: number;
  reason?: string;
  status: LeaveStatus;
  approved_by?: number;
  approved_by_username?: string;
  approved_at?: string;
  approved_on_behalf_of_user_id?: number | null;
  approved_on_behalf_of_username?: string | null;
  supervisor_username?: string | null;
  rejection_reason?: string;
  notes?: string;
  submitted_at?: string;
  username?: string;
  email?: string;
  certificate_file_name?: string | null;
  certificate_file_type?: string | null;
  /** Hours below zero the balance was going to end when the employee asked, and said yes. */
  requested_negative_hours?: NumericValue | null;
  /** Hours below zero the approver agreed to on approval. */
  negative_override_hours?: NumericValue | null;
  negative_override_by?: number | null;
  negative_override_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateLeaveRequest {
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  reason?: string;
  hours?: number;
  /** Partial-day leave window. When set the server derives `hours` and
   *  pre-checks for overlap with existing timesheet entries at submission. */
  start_time?: string; // HH:mm
  end_time?: string;   // HH:mm
  /** The employee has been told this takes their balance below zero and wants it anyway. */
  allow_negative_balance?: boolean;
}

/** How far below zero an approver can take a leave balance. Mirrors HHNodeServer `utils/negativeLeave.js`. */
export const MAX_NEGATIVE_LEAVE_HOURS = 100;

/**
 * What the server said about a balance going below zero, from a refused
 * request or approval.
 *
 * - `NEGATIVE_BALANCE_CONFIRM`: the employee is asking; send it again with
 *   `allow_negative_balance` once they say yes.
 * - `NEGATIVE_BALANCE_OVERRIDE_REQUIRED`: the approver is approving; same.
 * - `NEGATIVE_BALANCE_LIMIT`: more than {@link MAX_NEGATIVE_LEAVE_HOURS} below
 *   zero. Nobody can say yes to it here.
 */
export interface NegativeBalanceRefusal {
  code: 'NEGATIVE_BALANCE_CONFIRM' | 'NEGATIVE_BALANCE_OVERRIDE_REQUIRED' | 'NEGATIVE_BALANCE_LIMIT';
  message: string;
  hours_below_zero: number;
  balance_after: number;
}

export function negativeBalanceRefusal(err: unknown): NegativeBalanceRefusal | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code !== 'NEGATIVE_BALANCE_CONFIRM'
    && err.code !== 'NEGATIVE_BALANCE_OVERRIDE_REQUIRED'
    && err.code !== 'NEGATIVE_BALANCE_LIMIT') return null;
  const d = err.details ?? {};
  return {
    code: err.code,
    message: err.message,
    hours_below_zero: Number(d.hours_below_zero) || 0,
    balance_after: Number(d.balance_after) || 0,
  };
}

/** Whether the refusal is one the person can answer yes to. */
export function canGoNegative(r: NegativeBalanceRefusal | null): boolean {
  return !!r && r.code !== 'NEGATIVE_BALANCE_LIMIT';
}

export interface LeaveBalance {
  id: number;
  user_id: number;
  organisation_id: number;
  leave_type: LeaveType;
  total_hours: NumericValue;
  used_hours: NumericValue;
  available_hours: NumericValue;
  created_at: string;
  updated_at: string;
}

export interface LeaveBalanceAuditEntry {
  id: number;
  leave_balance_id: number;
  user_id: number;
  organisation_id: number;
  leave_type: LeaveType;
  field_changed: 'total_hours' | 'used_hours';
  old_value: string | number;
  new_value: string | number;
  delta: string | number;
  reason: string;
  changed_by_user_id: number;
  changed_by_username: string;
  changed_by_first_name?: string | null;
  changed_by_last_name?: string | null;
  changed_by_ip?: string | null;
  created_at: string;
}

/**
 * Where an employee's balances come from. With `source: 'xero'`, the types in
 * `owned_types` are mirrored from Xero: total = Xero's balance as at the last
 * posted pay run, used = leave booked but not yet paid (in Xero, or approved
 * here and not yet sent). Those types are not accrued or edited here.
 */
export interface LeaveBalanceSource {
  source: 'local' | 'xero';
  synced_at?: string | null;
  sync_error?: string | null;
  owned_types: LeaveType[];
  linked?: boolean;
  /**
   * Only with `source: 'local'`: whether HR can copy this employee's balances
   * from Xero by hand. Absent or null when Xero Payroll is not connected.
   */
  xero_import?: { available: boolean; reason: string | null } | null;
}

/** One leave type in a Xero import preview. */
export interface XeroImportRow {
  leave_type: LeaveType;
  current_total_hours: number;
  current_used_hours: number;
  xero_total_hours: number;
  xero_used_hours: number;
  changed: boolean;
}

export interface XeroImportResult {
  applied: LeaveType[];
  failed: { leave_type: LeaveType; error: string }[];
  unchanged: LeaveType[];
}

export interface AdminBalancesPayload {
  user: {
    id: number;
    username: string;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
  };
  balances: LeaveBalance[];
  source?: LeaveBalanceSource;
}

export interface EditEntitlementPayload {
  total_hours?: number;
  used_hours?: number;
  reason: string;
}

export interface EditEntitlementResult {
  balance: LeaveBalance;
  before: { id: number; leave_type: LeaveType; total_hours: number; used_hours: number };
  audit_entries: LeaveBalanceAuditEntry[];
}

export interface TilBalance {
  accrued_hours: number;
  used_hours: NumericValue;
  available_hours: NumericValue;
  last_updated: string | null;
}

export interface ProjectedBalancePreview {
  currentBalance: number;
  projectedBalance: number;
  projectedAccrual: number;
  projectedWorkHours: number;
  accrualRatePerHour: number;
  projectionApplied: boolean;
}

export interface SickCertCheck {
  required: boolean;
  reasons: string[];
  enforcement: 'info_only' | 'warn_approver' | 'block_approval';
}

/** Long service leave is shown to the employee only once this much is available. */
export const LONG_SERVICE_MIN_SHOWN_HOURS = 200;

/**
 * The balances an employee sees of their own. Long service leave accrues from
 * the first day and cannot be taken for years, so a small balance is left out.
 * HR's view (`adminGetBalances`) is not filtered.
 */
export function shownToEmployee(balances: LeaveBalance[]): LeaveBalance[] {
  return balances.filter(b =>
    b.leave_type !== 'long_service' || Number(b.available_hours) >= LONG_SERVICE_MIN_SHOWN_HOURS
  );
}

class LeaveService {
  // Create leave request
  async createRequest(data: CreateLeaveRequest): Promise<ApiResponse<LeaveRequest>> {
    return apiService.post('/leave/requests', data);
  }

  // Get user's leave requests
  async getRequests(params: {
    status?: LeaveStatus;
    year?: number;
  } = {}): Promise<ApiResponse<LeaveRequest[]>> {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.year) queryParams.append('year', params.year.toString());

    const queryString = queryParams.toString();
    const url = queryString ? `/leave/requests?${queryString}` : '/leave/requests';
    
    return apiService.get(url);
  }

  // Cancel leave request
  async cancelRequest(id: number): Promise<ApiResponse<LeaveRequest>> {
    return apiService.patch(`/leave/requests/${id}/cancel`);
  }

  /**
   * Give back one day of approved leave because it is being worked after all.
   *
   * Separate from `cancelRequest` on purpose: only the named day is released,
   * so the rest of a week off survives. Working the Wednesday must not cost the
   * employee the Thursday and Friday they still intend to take.
   */
  async releaseDay(
    id: number,
    date: string,
    reason?: string
  ): Promise<ApiResponse<ReleaseLeaveDayResult>> {
    return apiService.post(`/leave/requests/${id}/release-day`, { date, reason });
  }

  // Admin: Get all leave requests
  async getAllRequests(params: {
    status?: LeaveStatus;
    userId?: number;
  } = {}): Promise<ApiResponse<LeaveRequest[]>> {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.userId) queryParams.append('userId', params.userId.toString());

    const queryString = queryParams.toString();
    const url = queryString ? `/leave/admin/requests?${queryString}` : '/leave/admin/requests';
    
    return apiService.get(url);
  }

  // Admin: Review leave request
  async reviewRequest(id: number, data: {
    status: 'approved' | 'rejected';
    rejection_reason?: string;
    /** The approver agrees to the balance going below zero. */
    allow_negative_balance?: boolean;
  }): Promise<ApiResponse<LeaveRequest>> {
    return apiService.patch(`/leave/admin/requests/${id}/review`, data);
  }

  // Supervisor: Get pending approvals count
  async getPendingApprovalsCount(): Promise<ApiResponse<{ count: number }>> {
    return apiService.get('/leave/supervisor/pending-count');
  }

  // Get all leave balances for current user
  async getBalances(): Promise<ApiResponse<LeaveBalance[]> & { meta?: LeaveBalanceSource }> {
    const res: ApiResponse<LeaveBalance[]> & { meta?: LeaveBalanceSource } = await apiService.get('/leave/balances');
    if (res?.success && Array.isArray(res.data)) res.data = shownToEmployee(res.data);
    return res;
  }

  // Get current user's Time in Lieu balance
  async getTilBalance(): Promise<ApiResponse<TilBalance>> {
    return apiService.get('/leave/til-balance');
  }

  // Supervisor: Get approvals for supervised employees.
  // `scope: 'hr'` (HR licence holders only) returns every pending org-wide
  // request — used by the HR Override tab.
  async getSupervisorApprovals(params: {
    status?: LeaveStatus;
    scope?: 'mine' | 'hr';
  } = {}): Promise<ApiResponse<LeaveRequest[]>> {
    const queryParams = new URLSearchParams();

    if (params.status) queryParams.append('status', params.status);
    if (params.scope) queryParams.append('scope', params.scope);

    const queryString = queryParams.toString();
    const url = queryString ? `/leave/supervisor/approvals?${queryString}` : '/leave/supervisor/approvals';

    return apiService.get(url);
  }

  // Upload doctor's certificate for a leave request
  async uploadCertificate(requestId: number, file: File): Promise<ApiResponse<{ certificate_file_name: string; certificate_file_type: string }>> {
    const formData = new FormData();
    formData.append('certificate', file);
    const token = getToken();
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
    const response = await fetch(`${baseUrl}/leave/requests/${requestId}/certificate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error?.error?.message || 'Certificate upload failed');
    }
    return response.json();
  }

  // Get certificate URL (for authenticated fetch)
  getCertificateUrl(requestId: number): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
    return `${baseUrl}/leave/requests/${requestId}/certificate`;
  }

  // Delete certificate from a leave request
  async deleteCertificate(requestId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/leave/requests/${requestId}/certificate`);
  }

  // HR/Admin: Get all leave balances for an employee
  async adminGetBalances(userId: number): Promise<ApiResponse<AdminBalancesPayload>> {
    return apiService.get(`/leave/admin/balances/${userId}`);
  }

  /** HR: one employee's balances as Xero has them, beside ours. Writes nothing. */
  async adminPreviewXeroImport(userId: number): Promise<ApiResponse<{ rows: XeroImportRow[] }>> {
    return apiService.get(`/leave/admin/balances/${userId}/xero-import`, { timeout: 60_000 });
  }

  /** HR: copy the previewed Xero figures in. Refused (409) if Xero moved since. */
  async adminApplyXeroImport(userId: number, expected: XeroImportRow[]): Promise<ApiResponse<XeroImportResult>> {
    return apiService.post(`/leave/admin/balances/${userId}/xero-import`, { expected }, undefined, { timeout: 60_000 });
  }

  // HR/Admin: Edit a single leave balance row (with required reason)
  async adminEditBalance(
    userId: number,
    leaveType: LeaveType,
    payload: EditEntitlementPayload
  ): Promise<ApiResponse<EditEntitlementResult>> {
    return apiService.patch(`/leave/admin/balances/${userId}/${leaveType}`, payload);
  }

  // HR/Admin: Paginated audit history for an employee's leave balances
  async adminGetBalanceAudit(
    userId: number,
    params: { leave_type?: LeaveType; limit?: number; offset?: number } = {}
  ): Promise<ApiResponse<LeaveBalanceAuditEntry[]>> {
    const qp = new URLSearchParams();
    if (params.leave_type) qp.append('leave_type', params.leave_type);
    if (params.limit != null) qp.append('limit', String(params.limit));
    if (params.offset != null) qp.append('offset', String(params.offset));
    const qs = qp.toString();
    return apiService.get(`/leave/admin/balances/${userId}/audit${qs ? `?${qs}` : ''}`);
  }

  // Preview projected balance at leave start date
  async previewProjectedBalance(
    leaveType: LeaveType,
    startDate: string
  ): Promise<ApiResponse<ProjectedBalancePreview>> {
    return apiService.get(`/leave/preview-balance?leave_type=${leaveType}&start_date=${startDate}`);
  }

  // Pre-check whether a doctor's certificate is required for a proposed
  // personal-leave range. Returns the triggered reasons and the org's
  // enforcement mode so the leave form can render the right callout.
  async checkSickCert(
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<SickCertCheck>> {
    return apiService.get(`/leave/cert-check?start_date=${startDate}&end_date=${endDate}`);
  }

  // Per-date schedule + worked-hours summary used by the partial-day leave
  // helper to pre-fill the time window for the rest of the workday.
  async getDayInfo(date: string): Promise<ApiResponse<LeaveDayInfo>> {
    return apiService.get(`/leave/day-info?date=${encodeURIComponent(date)}`);
  }
}

/** One contiguous block of a day, in org-local HH:mm. */
export interface LeaveDaySegment {
  start_time: string;
  end_time: string;
  /** Raw wall-clock length of the block. */
  span_hours: number;
  /**
   * Bookable leave hours for this block — the span scaled down so that all of
   * a day's segments together add up to exactly the day's shortfall (the
   * unpaid break is inside the span but isn't paid). A segment with
   * `hours === 0` is the break itself and must not be offered as leave.
   */
  hours: number;
}

export interface LeaveDayInfo {
  date: string;
  is_working_day: boolean;
  scheduled_start_time: string | null; // HH:mm
  scheduled_end_time:   string | null; // HH:mm
  scheduled_hours: number;
  worked_hours: number;
  earliest_clock_in_time: string | null; // HH:mm in org timezone
  latest_clock_out_time: string | null;  // HH:mm in org timezone
  remaining_scheduled_hours: number;
  /** Worked blocks, merged and clipped to the scheduled day. */
  worked_segments: { start_time: string; end_time: string }[];
  /**
   * The parts of the scheduled day NOT already covered by work. A short day
   * can be short at either end (late in, early out, or both), so this is a
   * list — drive the leave form off it rather than off a single clock time.
   */
  uncovered_segments: LeaveDaySegment[];
}

export const leaveService = new LeaveService();
export default leaveService;